import { Injectable } from '@angular/core';
import { UserService } from 'app/shared/api/technical-data';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { v4 } from 'uuid';
import { SystemUser } from '../models/systemUser';
import { SystemUserSettings } from '../models/systemUserSettings';
import { EnvironmentService } from './env.service';
import { SystemUserData } from './system.user.data';
import { CompanyService, DiConUserResultTO, UserMappingType, UserSettingsTO } from '../../api/generated/v2/incident';

@Injectable({
  providedIn: 'root'
})
export class UserInfoService {

  constructor(private userService: UserService) {
    this.userService.configuration.basePath = EnvironmentService.getEnvironment().incidentService;
  }

  public getSystemUsers(internal?: boolean): Observable<SystemUser[]> {
    return !!internal ?
      of(SystemUserData.SYSTEM_USERS) :
      this.getUsersOfControlOrganization().pipe(
        map(diconUserResultTO => diconUserResultTO.data),
        map((diconUserTOArr) =>
        diconUserTOArr.map((diconUser) => new SystemUser(diconUser.mail, diconUser.firstname, diconUser.lastname))));
  }

  public getSystemUserSettings(userId: string, internal?: boolean): Observable<SystemUserSettings> {
    return !!internal ?
      of(SystemUserData.findSystemUserSettingsByEmail(userId)) :
      this.getUserSettings(userId).pipe(
        map(res => res as SystemUserSettings));
  }

  public getUsersOfControlOrganization(): Observable<DiConUserResultTO> {
    return this.userService.findDiconUsersOfControlOrganization();
  }

  public getUserPhoto(email: string): Observable<any> {
    return this.userService.getUserPhoto(email);
  }

  public getUserSettings(userId: string): Observable<any> {
    return this.userService.getUserSettings(userId);
  }

  public getAllUsersGroup(): Observable<any> {
    return forkJoin({
      internalUsers: this.getInternalUsers(),
      externalUsers: this.getExternalUsers(),
      viewerUsers: this.getViewerUsers()
    }).pipe(
      map(({ internalUsers, externalUsers, viewerUsers }) => {
        const internalUsersList = internalUsers.data?.map(user => ({
          ...user,
          type: UserMappingType.InternalUser
        }));
        const viewerUsersList = viewerUsers.data?.map(user => ({
          ...user,
          type: UserMappingType.ViewerUser
        }));
        const externalUsersList = externalUsers.data?.map(user => ({
          ...user,
          type: UserMappingType.ExternalUser
        }));
        return [
          ...internalUsersList,
          ...viewerUsersList,
          ...externalUsersList
        ];
      })
    ).pipe(share());
  }

  public saveUserSettings(email: string, userSettings: UserSettingsTO): Observable<any> {
    return this.userService.updateUserSettings(email, userSettings, 'response');
  }

  public deleteUserSettings(email: string): Observable<any> {
    return this.userService.deleteUserSettings(email, 'response');
  }

  public getEmailFavorites(email: string): Observable<string[]> {
    return of(SystemUserData.findEmailFavoritesByEmail(email));
  }

  private getInternalUsers(): Observable<DiConUserResultTO> {
    return this.userService.findDiconUsersOfControlOrganization().pipe(
      catchError(error => {
        console.error('Error fetching user settings:', error);
        // You can return a default value or rethrow the error
        return of({
          totalCount: 0,
          data: []
        });
      })
    );
  }

  public getExternalUsers(): Observable<DiConUserResultTO> {
    return this.userService.findDiconUsersOfExternalGroup().pipe(
      catchError(error => {
        console.error('Error fetching user settings:', error);
        // You can return a default value or rethrow the error
        return of({
          totalCount: 0,
          data: []
        });
      })
    );
  }

  public getViewerUsers(): Observable<DiConUserResultTO> {
    return this.userService.findDiconUsersOfViewerGroup().pipe(
      catchError(error => {
        console.error('Error fetching user settings:', error);
        // You can return a default value or rethrow the error
        return of({
          totalCount: 0,
          data: []
        });
      })
    );
  }
}
